import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Group,
  LoadingOverlay,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import PreviousPageButton from '#/components/ui/PreviousPageButton.tsx';
import { notifySuccess } from '#/utils/NotifySuccess.tsx';
import { ApproveConfirmModal } from '../components/ApproveConfirmModal.tsx';
import { DocumentFilePreview } from '../components/DocumentFilePreview.tsx';
import { DrivingLicenseForm } from '../components/DrivingLicenseForm.tsx';
import { InsurancePolicyForm } from '../components/InsurancePolicyForm.tsx';
import { RejectConfirmModal } from '../components/RejectConfirmModal.tsx';
import { ReviewErrorModal } from '../components/ReviewErrorModal.tsx';
import { VehicleRegistrationForm } from '../components/VehicleRegistrationForm.tsx';
import { DOCUMENT_TYPE_LABEL } from '../constants/documentTypes.ts';
import { useAccountDocumentDetail } from '../hooks/useAccountDocumentDetail.ts';
import { useApproveAccountDocument } from '../hooks/useApproveAccountDocument.ts';
import { useRejectAccountDocument } from '../hooks/useRejectAccountDocument.ts';
import type {
  AccountDocument,
  ApproveAccountDocumentParams,
} from '../model.ts';
import { buildReviewErrorInfo } from '../utils/apiError.ts';
import type { ReviewErrorInfo } from '../utils/apiError.ts';

const REVIEW_FORM_ID = 'document-review-form';

const READABLE_DISABLED_STYLES = {
  input: {
    color: 'var(--mantine-color-text)',
    opacity: 1,
  },
} as const;

type ReviewFormProps = {
  accountDocument: AccountDocument;
  readOnly: boolean;
  onValidSubmit: (params: ApproveAccountDocumentParams) => void;
};

const ReviewForm = ({
  accountDocument,
  readOnly,
  onValidSubmit,
}: ReviewFormProps) => {
  switch (accountDocument.documentType) {
    case 'VEHICLE_REGISTRATION':
      return (
        <VehicleRegistrationForm
          formId={REVIEW_FORM_ID}
          initialInfo={accountDocument.vehicleRegistrationInfo}
          readOnly={readOnly}
          onValidSubmit={(info) =>
            onValidSubmit({ documentType: 'VEHICLE_REGISTRATION', info })
          }
        />
      );
    case 'DRIVING_LICENSE':
      return (
        <DrivingLicenseForm
          formId={REVIEW_FORM_ID}
          initialInfo={accountDocument.drivingLicenseInfo}
          readOnly={readOnly}
          onValidSubmit={(info) =>
            onValidSubmit({ documentType: 'DRIVING_LICENSE', info })
          }
        />
      );
    case 'INSURANCE_POLICY':
      return (
        <InsurancePolicyForm
          formId={REVIEW_FORM_ID}
          initialInfo={accountDocument.insurancePolicyInfo}
          readOnly={readOnly}
          onValidSubmit={(info) =>
            onValidSubmit({ documentType: 'INSURANCE_POLICY', info })
          }
        />
      );
  }
};

const DocumentReviewDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const listSearch = searchParams.toString();
  const listPath = listSearch
    ? `/driver-management/document-review?${listSearch}`
    : '/driver-management/document-review';
  const navigate = useNavigate();

  const { data: accountDocument, isLoading } = useAccountDocumentDetail(id);

  const { mutate: approve, isPending: isApproving } =
    useApproveAccountDocument();
  const { mutate: reject, isPending: isRejecting } = useRejectAccountDocument();

  const [pendingApproval, setPendingApproval] =
    useState<ApproveAccountDocumentParams | null>(null);
  const [
    isRejectModalOpened,
    { open: openRejectModal, close: closeRejectModal },
  ] = useDisclosure(false);
  const [reviewError, setReviewError] = useState<ReviewErrorInfo | null>(null);

  if (isLoading) {
    return (
      <LoadingOverlay visible loaderProps={{ color: 'cyan', type: 'bars' }} />
    );
  }

  if (!id || !accountDocument) {
    return (
      <>
        <PreviousPageButton href={listPath} content="返回駕駛資料審核" />
        <Paper radius="lg" p="xl" shadow="xs">
          <Text c="dimmed">
            查無此文件，請回列表重新選擇，若持續失敗請聯絡工程團隊。
          </Text>
        </Paper>
      </>
    );
  }

  const documentTypeLabel = DOCUMENT_TYPE_LABEL[accountDocument.documentType];
  const accountLabel =
    accountDocument.account.email || accountDocument.account.id;
  const isReadOnly = accountDocument.reviewState !== 'PENDING_REVIEW';

  const backToList = () => navigate(listPath);

  const handleApprove = () => {
    if (!pendingApproval) return;

    approve(
      { id, params: pendingApproval },
      {
        onSuccess: () => {
          setPendingApproval(null);
          notifySuccess(`已將${documentTypeLabel}標記為審核成功`);
          backToList();
        },
        onError: (error) => {
          setPendingApproval(null);
          setReviewError(buildReviewErrorInfo(error));
        },
      },
    );
  };

  const handleReject = (reason: string) => {
    reject(
      { id, reason },
      {
        onSuccess: () => {
          closeRejectModal();
          notifySuccess(`已將${documentTypeLabel}標記為審核失敗`);
          backToList();
        },
        onError: (error) => {
          closeRejectModal();
          setReviewError(buildReviewErrorInfo(error));
        },
      },
    );
  };

  return (
    <div>
      <PreviousPageButton href={listPath} content="返回駕駛資料審核" />

      <div>
        <Paper
          radius="lg"
          shadow="xs"
          h="calc(100vh - 140px)"
          mih={560}
          style={{ overflow: 'hidden' }}
        >
          <Group align="stretch" h="100%" gap={0} wrap="nowrap">
            <Box p="md" style={{ flex: 1, minWidth: 0 }}>
              <DocumentFilePreview files={accountDocument.files} />
            </Box>

            <Box w={400} p="xl" style={{ flexShrink: 0 }}>
              <Stack h="100%" gap="md">
                <Title order={3}>
                  {isReadOnly ? '檢視' : '審核'}
                  {documentTypeLabel}
                </Title>

                <TextInput
                  label="駕駛中心 Email"
                  value={accountLabel}
                  disabled
                  readOnly
                  styles={READABLE_DISABLED_STYLES}
                />

                <ScrollArea style={{ flex: 1 }} offsetScrollbars>
                  <ReviewForm
                    accountDocument={accountDocument}
                    readOnly={isReadOnly}
                    onValidSubmit={setPendingApproval}
                  />
                </ScrollArea>

                {!isReadOnly && (
                  <Group justify="flex-end">
                    <Button
                      variant="light"
                      color="red"
                      onClick={openRejectModal}
                      disabled={isApproving || isRejecting}
                    >
                      審核失敗
                    </Button>
                    <Button
                      type="submit"
                      form={REVIEW_FORM_ID}
                      color="green"
                      disabled={isApproving || isRejecting}
                    >
                      審核成功
                    </Button>
                  </Group>
                )}
              </Stack>
            </Box>
          </Group>
        </Paper>
      </div>

      <ApproveConfirmModal
        opened={Boolean(pendingApproval)}
        accountLabel={accountLabel}
        documentTypeLabel={documentTypeLabel}
        isPending={isApproving}
        onCancel={() => setPendingApproval(null)}
        onConfirm={handleApprove}
      />

      <RejectConfirmModal
        opened={isRejectModalOpened}
        accountLabel={accountLabel}
        documentTypeLabel={documentTypeLabel}
        isPending={isRejecting}
        onCancel={closeRejectModal}
        onConfirm={handleReject}
      />

      <ReviewErrorModal
        info={reviewError}
        onClose={() => setReviewError(null)}
        onBackToList={() => {
          setReviewError(null);
          backToList();
        }}
      />
    </div>
  );
};

export default DocumentReviewDetailPage;
